import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function SetPermissions({
  checkedItems,
  setCheckedItems,
  selectedLocation,
  locationData,
  setSelectedLocation,
}) {
  const dispatch = useDispatch();

  /*   All States
   ********************************************* */

  const [loading, setLoading] = useState(false);
  const [moduleGroupedPermissions, setModuleGroupedPermissions] = useState({});
  const [activeModule, setActiveModule] = useState(null);
  const [availableModules, setAvailableModules] = useState([]);

  const loadingOn = () => {
    setLoading(true);
  };
  const loadingOff = () => {
    setLoading(false);
  };

  /*   All Functions
   ********************************************* */

  const handleLocationChecked = (val) => {
    if (selectedLocation?.includes(val)) {
      setSelectedLocation((prev) => prev.filter((item) => item != val));
    } else {
      setSelectedLocation([...selectedLocation, val]);
    }
  };

  const handleAllLocationChecked = () => {
    if (selectedLocation?.length == 0) {
      setSelectedLocation((prev) => locationData.map((item) => item.id));
    } else {
      setSelectedLocation([]);
    }
  };

  const organizePermissionsByModule = (permissions) => {
    // First group by module, then by permGroup
    const moduleGroups = {};
    const modules = new Set();

    permissions.forEach((item) => {
      const module = item.permMainModule || "Sales";
      modules.add(module);

      if (!moduleGroups[module]) {
        moduleGroups[module] = {};
      }

      if (!moduleGroups[module][item.permGroup]) {
        moduleGroups[module][item.permGroup] = [];
      }

      moduleGroups[module][item.permGroup].push({
        id: item.id,
        permName: item.permName,
        permDescription: item.permDescription,
      });
    });

    // Set available modules and default active module
    const moduleArray = Array.from(modules);
    setAvailableModules(moduleArray);
    if (moduleArray.length > 0 && !activeModule) {
      setActiveModule(moduleArray[0]);
    }

    return moduleGroups;
  };

  const GetUserPermissionList = async () => {
    try {
      loadingOn();
      const { data } = await API.get("/permissions", { headers: authHeader() });
      const organized = organizePermissionsByModule(data);
      setModuleGroupedPermissions(organized);
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  const handlePermissionChange = (permName, isChecked) => {
    if (isChecked) {
      // Add to checked items
      setCheckedItems((prev) => [...prev, permName]);
    } else {
      // Remove from checked items
      setCheckedItems((prev) => prev.filter((item) => item !== permName));
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      GetUserPermissionList();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  // Get current module's permission groups
  const currentModulePermissions = activeModule
    ? moduleGroupedPermissions[activeModule] || {}
    : {};

  return (
    <div>
      {loading && <Spinner />}
      <div className="">
        {/* Module Selection Tabs using the provided UI style */}
        <ul className="nav nav-tabs nav-bordered border-0 d-flex flex-grow-1 mb-4">
          {availableModules.map((module) => (
            <li
              key={module}
              onClick={() => setActiveModule(module)}
              className="nav-item"
            >
              <button
                type="button"
                className={
                  activeModule === module
                    ? `btn btn-primary active bg-primary text-white`
                    : "nav-link"
                }
              >
                <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                <span className="d-none d-md-block">{module}</span>
              </button>
            </li>
          ))}
        </ul>

        {activeModule && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Location Permissions - only show in Sales module */}
            {activeModule === "Sales" && (
              <div className="border py-0 px-4 rounded-lg">
                <h3 className="text-xl font-semibold mb-2">Location</h3>
                <ul className="list-disc pl-5">
                  {/* Select All checkbox */}
                  <li className="mb-1">
                    <label>
                      <input
                        type="checkbox"
                        checked={
                          selectedLocation?.length === locationData?.length
                        }
                        onChange={() => handleAllLocationChecked()}
                      />
                      <span className="ms-2">Select All</span>
                    </label>
                  </li>

                  {/* Individual location checkboxes */}
                  {locationData?.map((location) => (
                    <li key={location.id} className="mb-1">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedLocation?.includes(location.id)}
                          onChange={() => handleLocationChecked(location.id)}
                        />
                        <span className="ms-2">{location?.locationName}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Permission Groups for Current Module */}
            {Object.entries(currentModulePermissions).map(
              ([group, permissions]) => (
                <div key={group} className="border py-0 px-4 rounded-lg">
                  <h3 className="text-xl font-semibold mb-2">{group}</h3>
                  <ul className="list-disc pl-5">
                    {/* Check All checkbox for group */}
                    <li className="mb-1">
                      <label>
                        <input
                          type="checkbox"
                          checked={permissions.every((perm) =>
                            checkedItems.includes(perm.permName)
                          )}
                          onChange={(e) => {
                            const isChecked = e.target.checked;

                            permissions.forEach((perm) => {
                              if (perm.permName === "work_orders_dataEntry") {
                                handlePermissionChange(perm.permName, false);
                              } else {
                                handlePermissionChange(
                                  perm.permName,
                                  isChecked
                                );
                              }
                            });
                          }}
                        />
                        <span className="ms-2">Select All</span>
                      </label>
                    </li>

                    {/* Individual permissions */}
                    {permissions.map((perm) => (
                      <li key={perm.id} className="mb-1">
                        <label>
                          <input
                            type="checkbox"
                            checked={checkedItems.includes(perm.permName)}
                            onChange={(e) => {
                              if (
                                perm.permName === "work_orders_dataEntry" &&
                                e.target.checked
                              ) {
                                permissions.forEach((p) => {
                                  if (p.permName !== "work_orders_dataEntry") {
                                    handlePermissionChange(p.permName, false);
                                  }
                                });
                                handlePermissionChange(
                                  perm.permName,
                                  e.target.checked
                                );
                                return;
                              }

                              handlePermissionChange(
                                perm.permName,
                                e.target.checked
                              );
                            }}
                          />
                          <span className="ms-2">{perm.permDescription}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
